import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      const isDev = import.meta.env.DEV
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
          <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            {isDev ? this.state.error.message : 'An unexpected error occurred. Please try again.'}
          </p>
          {isDev && (
            <pre className="max-w-2xl overflow-auto rounded bg-muted p-4 text-left text-xs">
              {this.state.error.stack}
            </pre>
          )}
          <button
            className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground"
            onClick={() => {
              this.setState({ error: null })
              window.location.href = '/'
            }}
          >
            Go to dashboard
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
